import { reassignCrewAfterSplit } from "./crew";
import { getModuleDef } from "./modules";
import { CONDUIT_SUBDIV, markNetworksDirty, type ResourceNetwork } from "./networks";
import { jitterAngle } from "./physics/config";
import { moduleActiveWasteHeatW, moduleRadiateCoefW } from "./thermal";
import { clamp } from "../util/math";
import type { Conduit, EntityId, ModuleDef, Ship, ShipModule, World } from "./types";

export const CELL_SIZE = 8;

export function shipCellSize(ship: Ship): number {
  return ship.cellSizeM;
}

// Per-def cached [normal, rotated] cell dims. def.cells is immutable, so the
// two orientations are constants — returning shared frozen objects removes a
// {w,h} allocation from the single highest call-count path in the sim.
// Callers only ever read .w/.h (verified), so sharing is safe.
const _effCellsCache = new WeakMap<ModuleDef, [{ w: number; h: number }, { w: number; h: number }]>();

export function effectiveCells(
  def: ModuleDef,
  facing: 0 | 1 | 2 | 3,
): { w: number; h: number } {
  let pair = _effCellsCache.get(def);
  if (pair === undefined) {
    const normal = { w: def.cells.w, h: def.cells.h };
    const rotated = { w: def.cells.h, h: def.cells.w };
    Object.freeze(normal);
    Object.freeze(rotated);
    pair = [normal, rotated];
    _effCellsCache.set(def, pair);
  }
  return facing % 2 === 0 ? pair[0] : pair[1];
}

function sumLiveN(ship: Ship, fn: (m: ShipModule) => number): number {
  let acc = 0;
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    acc += fn(m);
  }
  return acc;
}

export interface ShipBlueprint {
  name: string;
  modules: { defId: string; gx: number; gy: number; facing?: 0 | 1 | 2 | 3 }[];
  conduits?: { sgx: number; sgy: number; type: "universal" | "heat"; facing?: 0 | 1 | 2 | 3 }[];
}

export function spawnShipFromBlueprint(
  world: World,
  blueprint: ShipBlueprint,
  spawn: { x: number; y: number; rot?: number },
): Ship {
  const modules: ShipModule[] = blueprint.modules.map(
    (m) => makeShipModule(m.defId, m.gx, m.gy, m.facing ?? 0),
  );
  const conduits: Conduit[] = (blueprint.conduits ?? []).map((c) => ({
    sgx: c.sgx, sgy: c.sgy, type: c.type, facing: c.facing ?? 0,
  }));

  const id: EntityId = world.nextId++;
  const ship: Ship = {
    id,
    name: blueprint.name,
    com: { x: 0, y: 0 },
    body: {
      pos: { x: spawn.x, y: spawn.y },
      vel: { x: 0, y: 0 },
      rot: spawn.rot ?? 0,
      angVel: 0,
      mass: 0,
      inertia: 0,
    },
    modules,
    conduits,
    throttle: 0,
    steer: 0,
    strafe: 0,
    fire1: false,
    powered: true,
    autoBrake: false,
    fireMode: "combat",
    isAsteroid: false,
    cellSizeM: CELL_SIZE,
    tradeWithShipId: null,
    boardedByShipId: null,
    credits: 0,
    collider: null,
  };

  recomputeShipMass(ship);
  fillBatteriesAndTanks(ship);
  markNetworksDirty(ship);

  world.ships.set(id, ship);
  return ship;
}

export function makeShipModule(
  defId: string,
  gx: number,
  gy: number,
  facing: 0 | 1 | 2 | 3,
  quality: number = 1,
): ShipModule {
  const def = getModuleDef(defId);
  const m: ShipModule = {
    defId,
    def,
    gx,
    gy,
    facing,
    hp: def.stats.hp,
    lastFiredAt: -Infinity,
    thrusterActivations: def.thrusters
      ? new Array(def.thrusters.length).fill(0)
      : [],
    tempK: 290,
    quality,
  };
  if (def.category === "docking") {
    m.userOff = true;
    m.docking = {
      attract: false,
      mode: "trade",
      peerShipId: null,
      peerModuleIdx: null,
      lockedAt: null,
    };
  }
  if (def.weapon && def.weapon.ammoMax != null) m.ammo = def.weapon.ammoMax;
  if (def.stats.cargoCapacityKg > 0) {
    m.cargo = new Map();
    m.cargoQualityKg = new Map();
  }
  if (def.stats.fuelCapacityKg > 0) m.fuelKg = 0;
  if (def.stats.batteryWh > 0) m.energyWh = 0;
  if (def.reactor && def.reactor.fuelBufferKg > 0) m.fuelBuffer = 0;
  return m;
}

export function fillBatteriesAndTanks(ship: Ship): void {
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const def = m.def;
    if (m.fuelKg != null) m.fuelKg = def.stats.fuelCapacityKg;
    if (m.energyWh != null) m.energyWh = def.stats.batteryWh;
    if (m.fuelBuffer != null && def.reactor) m.fuelBuffer = def.reactor.fuelBufferKg;
  }
}

/** Translate body.pos so surviving modules stay in their world pose after
 *  com.x/com.y shift. Caller must record oldComX/oldComY pre-recompute. */
function rebaseBodyAfterComShift(ship: Ship, oldComX: number, oldComY: number): void {
  const dCx = ship.com.x - oldComX;
  const dCy = ship.com.y - oldComY;
  if (dCx === 0 && dCy === 0) return;
  const rot = jitterAngle(ship.body.rot);
  const cr = Math.cos(rot);
  const sr = Math.sin(rot);
  const dWorldX = dCx * cr - dCy * sr;
  const dWorldY = dCx * sr + dCy * cr;
  ship.body.pos.x += dWorldX;
  ship.body.pos.y += dWorldY;
  const omega = ship.body.angVel;
  ship.body.vel.x += -omega * dWorldY;
  ship.body.vel.y +=  omega * dWorldX;
}

export function recomputeShipMass(ship: Ship): void {
  let mass = 0;
  let cxM = 0;
  let cyM = 0;
  const cs = shipCellSize(ship);
  for (const m of ship.modules) {
    if (m.hp <= 0 || m.illegal) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    const dm = def.stats.mass;
    mass += dm;
    cxM += (m.gx + eff.w / 2) * cs * dm;
    cyM += (m.gy + eff.h / 2) * cs * dm;
  }
  const comX = mass > 0 ? cxM / mass : 0;
  const comY = mass > 0 ? cyM / mass : 0;

  let inertia = 0;
  for (const m of ship.modules) {
    if (m.hp <= 0 || m.illegal) continue;
    const def = m.def;
    const eff = effectiveCells(def, m.facing);
    const mx = (m.gx + eff.w / 2) * cs - comX;
    const my = (m.gy + eff.h / 2) * cs - comY;
    const wM = eff.w * cs;
    const hM = eff.h * cs;
    inertia += def.stats.mass * (mx * mx + my * my + (wM * wM + hM * hM) / 12);
  }

  ship.body.mass = mass;
  ship.body.inertia = inertia;
  ship.com.x = comX;
  ship.com.y = comY;
  ship.collider = null;
  ship._extent = null;
}

/** Recompute mass after an in-place module change and translate body.pos so
 *  surviving modules keep their world pose. */
export function recomputeShipMassAndRebase(ship: Ship): void {
  const oldX = ship.com.x;
  const oldY = ship.com.y;
  recomputeShipMass(ship);
  rebaseBodyAfterComShift(ship, oldX, oldY);
}

export function canPlaceModule(
  ship: Ship,
  defId: string,
  gx: number,
  gy: number,
  facing: 0 | 1 | 2 | 3 = 0,
): boolean {
  const def = getModuleDef(defId);
  const eff = effectiveCells(def, facing);
  const ax0 = gx, ax1 = gx + eff.w;
  const ay0 = gy, ay1 = gy + eff.h;
  for (const m of ship.modules) {
    const md = m.def;
    const mef = effectiveCells(md, m.facing);
    const bx0 = m.gx, bx1 = m.gx + mef.w;
    const by0 = m.gy, by1 = m.gy + mef.h;
    if (!(ax1 <= bx0 || bx1 <= ax0 || ay1 <= by0 || by1 <= ay0)) return false;
  }
  return true;
}

export function moduleAtGrid(ship: Ship, gx: number, gy: number): ShipModule | null {
  for (const m of ship.modules) {
    const eff = effectiveCells(m.def, m.facing);
    if (gx >= m.gx && gx < m.gx + eff.w &&
        gy >= m.gy && gy < m.gy + eff.h) return m;
  }
  return null;
}

function rectsAdjacent(
  ax0: number, ay0: number, ax1: number, ay1: number,
  bx0: number, by0: number, bx1: number, by1: number,
): boolean {
  const xOverlap = ax0 < bx1 && bx0 < ax1;
  const yOverlap = ay0 < by1 && by0 < ay1;
  if (yOverlap && (ax1 === bx0 || bx1 === ax0)) return true;
  if (xOverlap && (ay1 === by0 || by1 === ay0)) return true;
  return false;
}

function moduleRect(m: ShipModule): { x0: number; y0: number; x1: number; y1: number } {
  const eff = effectiveCells(m.def, m.facing);
  return { x0: m.gx, y0: m.gy, x1: m.gx + eff.w, y1: m.gy + eff.h };
}

export function sharedEdgeCells(a: ShipModule, b: ShipModule): number {
  if (a.hp <= 0 || b.hp <= 0) return 0;
  const ra = moduleRect(a);
  const rb = moduleRect(b);
  if (ra.x1 === rb.x0 || rb.x1 === ra.x0) {
    return Math.max(0, Math.min(ra.y1, rb.y1) - Math.max(ra.y0, rb.y0));
  }
  if (ra.y1 === rb.y0 || rb.y1 === ra.y0) {
    return Math.max(0, Math.min(ra.x1, rb.x1) - Math.max(ra.x0, rb.x0));
  }
  return 0;
}

function connectedComponents(modules: ShipModule[]): number[][] {
  const n = modules.length;
  const visited = new Set<number>();
  const comps: number[][] = [];
  const rects = modules.map(moduleRect);
  for (let i = 0; i < n; i++) if (modules[i].hp <= 0) visited.add(i);
  for (let i = 0; i < n; i++) {
    if (visited.has(i)) continue;
    const comp: number[] = [];
    const queue: number[] = [i];
    visited.add(i);
    while (queue.length) {
      const cur = queue.shift() as number;
      comp.push(cur);
      const a = rects[cur];
      for (let j = 0; j < n; j++) {
        if (visited.has(j)) continue;
        const b = rects[j];
        if (rectsAdjacent(a.x0, a.y0, a.x1, a.y1, b.x0, b.y0, b.x1, b.y1)) {
          visited.add(j);
          queue.push(j);
        }
      }
    }
    comps.push(comp);
  }
  return comps;
}

/** Mark modules illegal: overlap (earlier wins) or smaller-than-main component. */
export function recomputeIllegal(ship: Ship): number {
  const mods = ship.modules;
  for (const m of mods) m.illegal = false;

  const claimed = new Map<string, number>();
  for (let i = 0; i < mods.length; i++) {
    const m = mods[i];
    if (m.hp <= 0) continue;
    const eff = effectiveCells(m.def, m.facing);
    let overlaps = false;
    const cells: string[] = [];
    for (let dx = 0; dx < eff.w && !overlaps; dx++) {
      for (let dy = 0; dy < eff.h && !overlaps; dy++) {
        const key = `${m.gx + dx},${m.gy + dy}`;
        if (claimed.has(key)) overlaps = true;
        else cells.push(key);
      }
    }
    if (overlaps) m.illegal = true;
    else for (const k of cells) claimed.set(k, i);
  }

  const live = mods.filter((m) => m.hp > 0 && !m.illegal);
  if (live.length > 1) {
    const components = connectedComponents(live);
    if (components.length > 1) {
      components.sort((a, b) => b.length - a.length);
      for (let i = 1; i < components.length; i++) {
        for (const idx of components[i]) live[idx].illegal = true;
      }
    }
  }

  let count = 0;
  for (const m of mods) if (m.illegal) count++;
  return count;
}

function cellSetForModules(mods: ShipModule[]): Set<string> {
  const out = new Set<string>();
  for (const m of mods) {
    const eff = effectiveCells(m.def, m.facing);
    for (let dy = 0; dy < eff.h; dy++) for (let dx = 0; dx < eff.w; dx++) {
      out.add(`${m.gx + dx},${m.gy + dy}`);
    }
  }
  return out;
}

function filterConduitsForCells(conduits: Conduit[], cells: Set<string>): Conduit[] {
  return conduits.filter((c) => cells.has(
    `${Math.floor(c.sgx / CONDUIT_SUBDIV)},${Math.floor(c.sgy / CONDUIT_SUBDIV)}`,
  ));
}

export function splitDisconnectedComponents(world: World, ship: Ship): Ship[] {
  const components = connectedComponents(ship.modules);
  if (components.length <= 1) return [];
  components.sort((a, b) => b.length - a.length);

  const cs = shipCellSize(ship);
  const oldComX = ship.com.x;
  const oldComY = ship.com.y;
  const oldPosX = ship.body.pos.x;
  const oldPosY = ship.body.pos.y;
  const oldVelX = ship.body.vel.x;
  const oldVelY = ship.body.vel.y;
  const oldRot = jitterAngle(ship.body.rot);
  const oldOmega = ship.body.angVel;
  const cr = Math.cos(oldRot);
  const sr = Math.sin(oldRot);
  const SEP_M_S = 1.5;

  const spawned: Ship[] = [];
  for (let i = 1; i < components.length; i++) {
    const indices = components[i];
    const compModules = indices.map((idx) => ship.modules[idx]);

    let mass = 0, cxM = 0, cyM = 0;
    for (const m of compModules) {
      const def = m.def;
      const eff = effectiveCells(def, m.facing);
      const dm = def.stats.mass;
      mass += dm;
      cxM += (m.gx + eff.w / 2) * cs * dm;
      cyM += (m.gy + eff.h / 2) * cs * dm;
    }
    const newComX = mass > 0 ? cxM / mass : 0;
    const newComY = mass > 0 ? cyM / mass : 0;

    const offLocalX = newComX - oldComX;
    const offLocalY = newComY - oldComY;
    const offWorldX = offLocalX * cr - offLocalY * sr;
    const offWorldY = offLocalX * sr + offLocalY * cr;

    let newVelX = oldVelX - oldOmega * offWorldY;
    let newVelY = oldVelY + oldOmega * offWorldX;
    const len = Math.hypot(offWorldX, offWorldY);
    if (len > 0) {
      newVelX += (offWorldX / len) * SEP_M_S;
      newVelY += (offWorldY / len) * SEP_M_S;
    }

    const compConduits = filterConduitsForCells(ship.conduits, cellSetForModules(compModules));

    const id: EntityId = world.nextId++;
    const newShip: Ship = {
      id,
      name: `${ship.name}-debris`,
      com: { x: 0, y: 0 },
      body: {
        pos: { x: oldPosX + offWorldX, y: oldPosY + offWorldY },
        vel: { x: newVelX, y: newVelY },
        rot: oldRot,
        angVel: oldOmega,
        mass: 0,
        inertia: 0,
      },
      modules: compModules,
      conduits: compConduits,
      throttle: 0,
      steer: 0,
      strafe: 0,
      fire1: false,
      powered: false,
      autoBrake: false,
      fireMode: ship.fireMode,
      isAsteroid: ship.isAsteroid,
      cellSizeM: ship.cellSizeM,
      tradeWithShipId: null,
      boardedByShipId: null,
      credits: 0,
      collider: null,
    };
    recomputeShipMass(newShip);
    markNetworksDirty(newShip);
    world.ships.set(id, newShip);
    spawned.push(newShip);
  }

  ship.modules = components[0].map((idx) => ship.modules[idx]);
  ship.conduits = filterConduitsForCells(ship.conduits, cellSetForModules(ship.modules));
  markNetworksDirty(ship);
  recomputeShipMass(ship);
  const mainOffX = ship.com.x - oldComX;
  const mainOffY = ship.com.y - oldComY;
  const mainOffWorldX = mainOffX * cr - mainOffY * sr;
  const mainOffWorldY = mainOffX * sr + mainOffY * cr;
  ship.body.pos.x = oldPosX + mainOffWorldX;
  ship.body.pos.y = oldPosY + mainOffWorldY;
  ship.body.vel.x = oldVelX - oldOmega * mainOffWorldY;
  ship.body.vel.y = oldVelY + oldOmega * mainOffWorldX;

  releaseAllDockingBondsForSplit(world, ship, spawned);
  reassignCrewAfterSplit(world, ship, spawned);

  return spawned;
}

/** Trimmed module indices would alias the wrong module on survivor pointers. */
function releaseAllDockingBondsForSplit(world: World, mainShip: Ship, spawned: Ship[]): void {
  const affected = new Set<EntityId>();
  affected.add(mainShip.id);
  for (const s of spawned) affected.add(s.id);

  for (const piece of [mainShip, ...spawned]) {
    for (const m of piece.modules) {
      const d = m.docking;
      if (!d) continue;
      d.peerShipId = null;
      d.peerModuleIdx = null;
      d.lockedAt = null;
    }
    piece.boardedByShipId = null;
    piece.tradeWithShipId = null;
  }

  for (const other of world.ships.values()) {
    if (affected.has(other.id)) continue;
    let touched = false;
    for (const m of other.modules) {
      const d = m.docking;
      if (!d || d.peerShipId == null) continue;
      if (affected.has(d.peerShipId)) {
        d.peerShipId = null;
        d.peerModuleIdx = null;
        d.lockedAt = null;
        touched = true;
      }
    }
    if (touched) {
      if (other.boardedByShipId != null && affected.has(other.boardedByShipId)) {
        other.boardedByShipId = null;
      }
      if (other.tradeWithShipId != null && affected.has(other.tradeWithShipId)) {
        other.tradeWithShipId = null;
      }
    }
  }
}

export function totalCargoCapacity(ship: Ship): number {
  return sumLiveN(ship, (m) => m.def.stats.cargoCapacityKg);
}

export function totalCargoMass(ship: Ship): number {
  return sumLiveN(ship, (m) => {
    if (!m.cargo) return 0;
    let kg = 0;
    for (const v of m.cargo.values()) kg += v;
    return kg;
  });
}

function totalFuelCapacity(ship: Ship): number {
  return sumLiveN(ship, (m) => m.def.stats.fuelCapacityKg);
}

export function totalCrewCapacity(ship: Ship): number {
  return sumLiveN(ship, (m) => m.def.stats.crewCapacity);
}

function shipFuelKg(ship: Ship): number {
  return sumLiveN(ship, (m) => m.fuelKg ?? 0);
}

function shipEnergyWh(ship: Ship): number {
  return sumLiveN(ship, (m) => m.energyWh ?? 0);
}

export function shipCargoKgOf(ship: Ship, resource: string): number {
  return sumLiveN(ship, (m) => m.cargo?.get(resource) ?? 0);
}

export function shipCargoBreakdown(ship: Ship): Map<string, number> {
  const out = new Map<string, number>();
  for (const m of ship.modules) {
    if (m.hp <= 0 || !m.cargo) continue;
    for (const [r, kg] of m.cargo) {
      if (kg <= 0) continue;
      out.set(r, (out.get(r) ?? 0) + kg);
    }
  }
  return out;
}

export function shipCargoQuality(ship: Ship, resource: string): number {
  let kg = 0, qkg = 0;
  for (const m of ship.modules) {
    if (m.hp <= 0 || !m.cargo) continue;
    kg += m.cargo.get(resource) ?? 0;
    qkg += m.cargoQualityKg?.get(resource) ?? 0;
  }
  return kg > 0 ? qkg / kg : 0;
}

function addCargoToModules(
  mods: Iterable<ShipModule>,
  resource: string,
  kg: number,
  quality: number,
): number {
  if (kg <= 0) return 0;
  let left = kg;
  for (const m of mods) {
    if (m.hp <= 0 || !m.cargo) continue;
    const cap = m.def.stats.cargoCapacityKg;
    if (cap <= 0) continue;
    let used = 0;
    for (const v of m.cargo.values()) used += v;
    const room = cap - used;
    if (room <= 0) continue;
    const give = Math.min(room, left);
    m.cargo.set(resource, (m.cargo.get(resource) ?? 0) + give);
    if (!m.cargoQualityKg) m.cargoQualityKg = new Map();
    m.cargoQualityKg.set(resource, (m.cargoQualityKg.get(resource) ?? 0) + give * quality);
    left -= give;
    if (left <= 0) break;
  }
  return kg - left;
}

/** Bypasses networks — debug/setup only. Real flow uses addCargoToNetwork. */
export function addCargoToShip(ship: Ship, resource: string, kg: number, quality: number): number {
  return addCargoToModules(ship.modules, resource, kg, quality);
}

export function addCargoToNetwork(net: ResourceNetwork, resource: string, kg: number, quality: number): number {
  return addCargoToModules(net.modules, resource, kg, quality);
}

export function drawCargoFromNetwork(
  net: ResourceNetwork,
  resource: string,
  kgWanted: number,
): number {
  if (kgWanted <= 0) return 0;
  let left = kgWanted;
  for (const m of net.modules) {
    if (m.hp <= 0 || !m.cargo) continue;
    const have = m.cargo.get(resource) ?? 0;
    if (have <= 0) continue;
    const take = Math.min(have, left);
    const newHave = have - take;
    m.cargo.set(resource, newHave);
    if (m.cargoQualityKg) {
      const qkg = m.cargoQualityKg.get(resource) ?? 0;
      m.cargoQualityKg.set(resource, qkg * (newHave / have));
    }
    left -= take;
    if (left <= 0) break;
  }
  return kgWanted - left;
}

export function drawFuelFromNetwork(net: ResourceNetwork, kgWanted: number): number {
  if (kgWanted <= 0) return 0;
  let left = kgWanted;
  for (const m of net.modules) {
    if (m.hp <= 0 || m.fuelKg == null || m.fuelKg <= 0) continue;
    const take = Math.min(m.fuelKg, left);
    m.fuelKg -= take;
    left -= take;
    if (left <= 0) break;
  }
  return kgWanted - left;
}

export function drawEnergyFromNetwork(net: ResourceNetwork, whWanted: number): number {
  if (whWanted <= 0) return 0;
  let left = whWanted;
  for (const m of net.modules) {
    if (m.hp <= 0 || m.disabled || m.energyWh == null || m.energyWh <= 0) continue;
    const take = Math.min(m.energyWh, left);
    m.energyWh -= take;
    left -= take;
    if (left <= 0) break;
  }
  return whWanted - left;
}

export function addEnergyToNetwork(net: ResourceNetwork, whWanted: number): number {
  if (whWanted <= 0) return 0;
  let left = whWanted;
  for (const m of net.modules) {
    if (m.hp <= 0 || m.disabled || m.energyWh == null) continue;
    const cap = m.def.stats.batteryWh;
    const room = cap - m.energyWh;
    if (room <= 0) continue;
    const give = Math.min(room, left);
    m.energyWh += give;
    left -= give;
    if (left <= 0) break;
  }
  return whWanted - left;
}

export function networkCargoCapacity(net: ResourceNetwork): number {
  let cap = 0;
  for (const m of net.modules) {
    if (m.hp <= 0) continue;
    cap += m.def.stats.cargoCapacityKg;
  }
  return cap;
}

export function networkCargoMass(net: ResourceNetwork): number {
  let kg = 0;
  for (const m of net.modules) {
    if (m.hp <= 0 || !m.cargo) continue;
    for (const v of m.cargo.values()) kg += v;
  }
  return kg;
}

function worldToShipLocal(ship: Ship, wx: number, wy: number): { x: number; y: number } {
  const dx = wx - ship.body.pos.x;
  const dy = wy - ship.body.pos.y;
  const rot = jitterAngle(-ship.body.rot);
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  return {
    x: dx * c - dy * s + ship.com.x,
    y: dx * s + dy * c + ship.com.y,
  };
}

export function worldToShipGrid(ship: Ship, wx: number, wy: number): { gx: number; gy: number } {
  const l = worldToShipLocal(ship, wx, wy);
  const cs = shipCellSize(ship);
  return { gx: Math.floor(l.x / cs), gy: Math.floor(l.y / cs) };
}

export function worldToShipSubGrid(ship: Ship, wx: number, wy: number): { sgx: number; sgy: number } {
  const l = worldToShipLocal(ship, wx, wy);
  const sub = shipCellSize(ship) / CONDUIT_SUBDIV;
  return { sgx: Math.floor(l.x / sub), sgy: Math.floor(l.y / sub) };
}

export function checkModuleEnableable(
  _ship: Ship,
  m: ShipModule,
): { ok: boolean; reason: string } {
  if (m.hp <= 0) return { ok: false, reason: "destroyed" };
  const def = m.def;
  if (def.stats.crew > 0 && m.manned === false) return { ok: false, reason: "no crew" };
  if (m.fuelStarved) return { ok: false, reason: "out of fuel" };
  if (def.category !== "reactor"
      && (def.stats.powerIdleW > 0 || def.stats.powerActiveW > 0)
      && !(m.powered ?? false)) {
    return { ok: false, reason: "no power" };
  }
  return { ok: true, reason: "" };
}

export function moduleOperational(_ship: Ship, m: ShipModule): boolean {
  return m.hp > 0 && !m.disabled && !m.illegal;
}

export function thrusterPortBlocked(
  ship: Ship,
  m: ShipModule,
  effW: number,
  effH: number,
  exhaustDx: number,
  exhaustDy: number,
): boolean {
  let sx0: number, sx1: number, sy0: number, sy1: number;
  if (exhaustDx !== 0) {
    sy0 = m.gy;
    sy1 = m.gy + effH;
    if (exhaustDx > 0) { sx0 = m.gx + effW; sx1 = sx0 + 1; }
    else               { sx1 = m.gx;        sx0 = sx1 - 1; }
  } else {
    sx0 = m.gx;
    sx1 = m.gx + effW;
    if (exhaustDy > 0) { sy0 = m.gy + effH; sy1 = sy0 + 1; }
    else               { sy1 = m.gy;        sy0 = sy1 - 1; }
  }
  for (const other of ship.modules) {
    if (other === m || other.hp <= 0) continue;
    const r = moduleRect(other);
    if (r.x0 < sx1 && sx0 < r.x1 && r.y0 < sy1 && sy0 < r.y1) return true;
  }
  return false;
}

const CARDINAL_VECS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 1, y: 0 }, { x: 0, y: 1 }, { x: -1, y: 0 }, { x: 0, y: -1 },
];

export function cardinalVec(dir: 0 | 1 | 2 | 3): { x: number; y: number } {
  return CARDINAL_VECS[dir];
}

export function rotateCardinal(dir: 0 | 1 | 2 | 3, by: 0 | 1 | 2 | 3): 0 | 1 | 2 | 3 {
  return ((dir + by) % 4) as 0 | 1 | 2 | 3;
}

export function moduleLocalCenter(ship: Ship, m: ShipModule): { x: number; y: number } {
  const eff = effectiveCells(m.def, m.facing);
  const cs = shipCellSize(ship);
  return {
    x: (m.gx + eff.w / 2) * cs - ship.com.x,
    y: (m.gy + eff.h / 2) * cs - ship.com.y,
  };
}

export function moduleWorldCenter(ship: Ship, m: ShipModule): { x: number; y: number } {
  const l = moduleLocalCenter(ship, m);
  const rot = jitterAngle(ship.body.rot);
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  return {
    x: ship.body.pos.x + l.x * c - l.y * s,
    y: ship.body.pos.y + l.x * s + l.y * c,
  };
}

/** AABB centroid in ship-local coords (origin = grid origin, not CoM). */
export function shipBlocksLocalCenter(ship: Ship): { x: number; y: number } | null {
  let any = false;
  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;
  const cs = shipCellSize(ship);
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const eff = effectiveCells(m.def, m.facing);
    const x0 = m.gx * cs;
    const y0 = m.gy * cs;
    const x1 = x0 + eff.w * cs;
    const y1 = y0 + eff.h * cs;
    if (x0 < minX) minX = x0;
    if (y0 < minY) minY = y0;
    if (x1 > maxX) maxX = x1;
    if (y1 > maxY) maxY = y1;
    any = true;
  }
  if (!any) return null;
  return { x: (minX + maxX) * 0.5, y: (minY + maxY) * 0.5 };
}

export function shipExtent(ship: Ship): number {
  // Depends only on com + module layout, both of which change exactly when
  // recomputeShipMass runs (where the cache is invalidated alongside the SDF
  // collider). Recomputed every tick across grid/plume/beam/viewport otherwise.
  if (ship._extent != null) return ship._extent;
  let maxR2 = 0;
  const cs = shipCellSize(ship);
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    const eff = effectiveCells(m.def, m.facing);
    const minX = m.gx * cs - ship.com.x;
    const minY = m.gy * cs - ship.com.y;
    const maxX = minX + eff.w * cs;
    const maxY = minY + eff.h * cs;
    const fx = Math.max(Math.abs(minX), Math.abs(maxX));
    const fy = Math.max(Math.abs(minY), Math.abs(maxY));
    const r2 = fx * fx + fy * fy;
    if (r2 > maxR2) maxR2 = r2;
  }
  const ext = maxR2 <= 0 ? cs * 2 : Math.sqrt(maxR2) + 0.5;
  ship._extent = ext;
  return ext;
}

export function rotateModuleCW(ship: Ship, m: ShipModule): boolean {
  m.facing = ((m.facing + 1) % 4) as 0 | 1 | 2 | 3;
  recomputeShipMassAndRebase(ship);
  markNetworksDirty(ship);
  return true;
}

interface ShipTelemetry {
  mass: number;
  hp: number;
  hpMax: number;
  crewRequired: number;
  powerBalanceW: number;
  powerOutW: number;
  powerIdleW: number;
  batteryWh: number;
  batteryCapWh: number;
  fuelKg: number;
  fuelCapKg: number;
  heatBalanceW: number;
  heatOutW: number;
  heatDissipateW: number;
  tempMaxK: number;
  tempMeanK: number;
  thrustForwardN: number;
  thrustLateralN: number;
}

export function shipTelemetry(ship: Ship): ShipTelemetry {
  let mass = 0, hp = 0, hpMax = 0, crewRequired = 0;
  let powerOutW = 0, powerIdleW = 0, batteryCapWh = 0;
  let heatOutW = 0, heatDissipateW = 0;
  let thrustForwardN = 0, thrustLateralN = 0;

  for (const m of ship.modules) {
    const def = m.def;
    mass += def.stats.mass;
    hpMax += def.stats.hp;
    if (m.hp <= 0) continue;
    hp += m.hp;
    crewRequired += def.stats.crew;
    batteryCapWh += def.stats.batteryWh;
    heatDissipateW += moduleRadiateCoefW(def);
    const operational = moduleOperational(ship, m);
    if (operational) {
      powerOutW += def.stats.powerOutW;
      powerIdleW += def.stats.powerIdleW;
      heatOutW += moduleActiveWasteHeatW(def);
    }

    if (def.thrusters && operational) {
      for (const port of def.thrusters) {
        const shipDir = cardinalVec(rotateCardinal(port.dir, m.facing));
        if (shipDir.x > 0) thrustForwardN += port.thrustN * shipDir.x;
        if (Math.abs(shipDir.y) > 0) thrustLateralN += port.thrustN;
      }
    }
  }
  thrustLateralN *= 0.5;

  let tempMaxK = 0;
  let tempWeighted = 0;
  let aliveMass = 0;
  for (const m of ship.modules) {
    if (m.hp <= 0) continue;
    if (m.tempK > tempMaxK) tempMaxK = m.tempK;
    const dm = m.def.stats.mass;
    tempWeighted += m.tempK * dm;
    aliveMass += dm;
  }
  const tempMeanK = aliveMass > 0 ? tempWeighted / aliveMass : 0;

  return {
    mass, hp, hpMax, crewRequired,
    powerBalanceW: powerOutW - powerIdleW,
    powerOutW, powerIdleW,
    batteryWh: shipEnergyWh(ship),
    batteryCapWh,
    fuelKg: shipFuelKg(ship),
    fuelCapKg: totalFuelCapacity(ship),
    heatBalanceW: heatDissipateW - heatOutW,
    heatOutW, heatDissipateW,
    tempMaxK, tempMeanK,
    thrustForwardN, thrustLateralN,
  };
}

export { clamp };
